/* eslint-disable import/prefer-default-export */
import isEmptyUtil from '@utils/isEmpty';
import {
  getDateDifference,
  getRegularDate,
  getRelativeDayOrCurrentDateWithTDays,
  todayTomorrowConfig
} from '@utils/dates';
import getItemFromSessionStorage from '@utils/getItemFromSessionStorage';
import getPreservedQueryParams from '@utils/getPreservedQueryParams';
import { PROGRAMME_LABEL_MAPPING } from '@constants/quiz';
import {
  OPTIONS_MAP,
  PROGRAMME_COMPOSITION,
  CBT_PROGRAMME_TYPE
} from './constants';

const avaliableTestOptions = {
  [OPTIONS_MAP.ALL]: true,
  [OPTIONS_MAP.UPCOMING]: true,
  [OPTIONS_MAP.ONGOING]: true
};

export const getValidTestType = (type) => {
  const parsedTestType = parseInt(type, 10);

  if (isEmptyUtil(parsedTestType)) {
    return OPTIONS_MAP.ONGOING;
  }

  if (avaliableTestOptions[parsedTestType]) {
    return parsedTestType;
  }

  return OPTIONS_MAP.ONGOING;
};

export const getLanguageString = (languages) => {
  return languages.reduce((acc, curr, index) => {
    let result = '';
    if (index === 0) {
      result = `${curr.displayName}`;
    } else if (index === languages.length - 1) {
      result = `${acc} & ${curr.displayName}`;
    } else {
      result = `${acc}, ${curr.displayName}`;
    }
    return result;
  }, '');
};

export const getEducatorsString = (educators) => {
  return educators.reduce((acc, curr, index) => {
    let result = '';
    if (index === 0) {
      result = `${curr.firstName} ${curr.lastName}`;
    } else if (index === educators.length - 1) {
      result = `${acc} & ${curr.firstName} ${curr.lastName}`;
    } else {
      result = `${acc}, ${curr.firstName} ${curr.lastName}`;
    }
    return result;
  }, '');
};

export const getDateStringInTestSeriesHeader = (startsAt, endsAt) => {
  const startsAtDate = new Date(startsAt);
  const endsAtDate = endsAt ? new Date(endsAt) : null;

  const startsOn = getRegularDate(startsAtDate);
  const currentDate = new Date();
  let dateString = '';
  if (currentDate.getTime() < startsAtDate.getTime()) {
    dateString = `Test starts ${startsOn}`;
  } else if (currentDate.getTime() > startsAtDate.getTime() && !endsAtDate) {
    dateString = `Started on ${startsOn}`;
  } else if (
    currentDate.getTime() > startsAtDate.getTime() &&
    currentDate.getTime() < endsAtDate.getTime()
  ) {
    dateString = `Started on ${startsOn}`;
  } else {
    dateString = `Ended on ${getRegularDate(endsAtDate)}`;
  }

  return dateString;
};

export const isPastTest = (endsAt = '') => {
  const endsAtDate = endsAt ? new Date(endsAt) : null;
  const currentDate = new Date();

  if (currentDate.getTime() > endsAtDate?.getTime()) {
    return true;
  }
  return false;
};

export const getTestSeriesDetailsAPIKey = (uid) => {
  return `test-series-svc/collection/v1/${uid}/details/`;
};

export const isCbtProgramme = (programmeType) =>
  programmeType === CBT_PROGRAMME_TYPE;

export const getProgrammeTypeLabel = (programmeSubtype, programmeType) => {
  const label = PROGRAMME_LABEL_MAPPING[programmeSubtype];
  if (!label) return '';
  if (isCbtProgramme(programmeType)) {
    return `NTA TEST SERIES (${label})`;
  }
  return label;
};

export const requiresCentreSelection = (programmeType, programmeComposition) =>
  programmeType === CBT_PROGRAMME_TYPE &&
  programmeComposition === PROGRAMME_COMPOSITION.ONLINE_OFFLINE;

export const getSkuDetailsAPIKey = (uid, goalUid) => {
  return `v1/subscriptions/user/store_skus/?content_type_id=501&object_uid=${uid}&goal_uid=${goalUid}&send_extra_details=True&centre_sub_required=True`;
};

export const getEnrollAPIKey = (testSeriesUID, goalUID) => {
  return `test-series-svc/collection/v1/${testSeriesUID}/enroll-state/?goal_uid=${goalUID}`;
};

export const getCbtLocationsAPIKey = (programmeUid) =>
  `v1/uplus/cbt/${programmeUid}/locations/`;

export const getCbtCentresAPIKey = (programmeUid, city) =>
  `v1/uplus/cbt/${programmeUid}/centres/?city=${encodeURIComponent(city)}`;

export const getCbtReserveAPIKey = () => 'v1/uplus/cbt/reserve/';

export const getCbtCentreChangeAPIKey = () => 'v1/uplus/cbt/centre-change/';

export const getCbtAllLocationsAPIKey = () => 'v1/uplus/cbt/all-locations/';

export const getCbtCentrePreferenceAPIKey = () =>
  'v1/uplus/cbt/centre-preference/';

export const getCbtReserveLearnerDetailsUid = (response) =>
  response?.data?.learnerDetailsUid ??
  response?.data?.learner_details_uid ??
  null;

export const getReserveErrorMessage = (error) => {
  const errorCode =
    typeof error === 'string'
      ? error
      : error?.error ?? error?.errorCode ?? error?.message;
  if (errorCode === 'no_seats_available') {
    return 'No seats available at this centre. Please choose another centre.';
  }
  if (errorCode === 'invalid_centre') {
    return 'Selected centre is unavailable. Please choose another centre.';
  }
  return error?.message || 'Unable to reserve seat. Please try again.';
};

export const getCentreChangeErrorMessage = (error) => {
  const errorCode =
    typeof error === 'string'
      ? error
      : error?.error_code ?? error?.error ?? error?.message;

  switch (errorCode) {
    case 'E1002':
      return 'Missing programme or centre details. Please try again.';
    case 'E4050':
    case 'E4053':
      return 'You are already booked at this centre.';
    case 'E4014':
      return 'Selected centre is unavailable for this test series.';
    case 'E4048':
    case 'E4052':
      return 'No centre changes left.';
    case 'E4046':
      return 'No confirmed booking found.';
    case 'E4013':
      return 'Test series not found.';
    case 'E4039':
      return 'Selected centre is full. Please choose another centre.';
    case 'E4040':
      return 'A request is already in progress. Please try again.';
    case 'E5000':
      return 'Something went wrong. Please try again.';
    default:
      return error?.message || 'Unable to change centre. Please try again.';
  }
};

export const getCentrePreferenceErrorMessage = (error) => {
  const errorCode =
    typeof error === 'string'
      ? error
      : error?.error_code ?? error?.error ?? error?.message;

  switch (errorCode) {
    case 'E2004':
      return 'Some of the required params are missing.';
    case 'E2912':
      return 'Test series not found.';
    case 'E001':
      return 'Something went wrong. Please try again.';
    default:
      return (
        error?.message ||
        'Unable to record preferred location. Please try again.'
      );
  }
};

export const reserveCbtSeatWithFeedback = async ({
  reserveApiCall,
  showSnackbar
}) => {
  const reserveResp = await reserveApiCall();

  if (reserveResp?.error) {
    showSnackbar({
      message: getReserveErrorMessage(reserveResp.error),
      type: 'error'
    });
    return null;
  }

  const learnerDetailsUid = getCbtReserveLearnerDetailsUid(reserveResp);
  if (!learnerDetailsUid) {
    showSnackbar({
      message: 'Unable to reserve seat. Please try again.',
      type: 'error'
    });
    return null;
  }

  return learnerDetailsUid;
};

export const handleCbtPurchaseClick = ({
  needsCentreSelection,
  isLoggedIn,
  showLoginDrawer,
  onOpenCbtVenueSelection,
  onProceed
}) => {
  if (needsCentreSelection) {
    if (!isLoggedIn) {
      showLoginDrawer();
      return;
    }
    onOpenCbtVenueSelection?.();
    return;
  }
  onProceed?.();
};

export const navigateToCheckoutWithLearnerDetails = ({
  goalSlug,
  goalUid,
  subscriptionUid,
  queryParams = {}
}) => {
  window.location.assign(
    `/goal/${goalSlug}/${goalUid}/subscribe/${subscriptionUid}${getPreservedQueryParams(
      queryParams
    )}`
  );
};

export const SEAT_AVAILABILITY_TIER = {
  LOW: 'low',
  MIDDLE: 'middle',
  HIGH: 'high'
};

export const getSeatAvailabilityTier = (availableSeats, totalSeats) => {
  const seats = availableSeats ?? 0;
  const percentage = (seats / totalSeats) * 100;

  if (percentage <= 30) return SEAT_AVAILABILITY_TIER.LOW;
  if (percentage <= 70) return SEAT_AVAILABILITY_TIER.MIDDLE;
  return SEAT_AVAILABILITY_TIER.HIGH;
};

export const getSeatAvailabilityLabel = (availableSeats, tier) => {
  const count = availableSeats ?? 0;
  if (tier === SEAT_AVAILABILITY_TIER.HIGH) {
    return `${count} SEATS AVAILABLE`;
  }
  return `${count} SEATS LEFT`;
};

export const getEnrollmentMessage = (date) => {
  if (!date) return 'N/A';
  const relativeDate = getRelativeDayOrCurrentDateWithTDays(
    date,
    true,
    7,
    true
  );
  const isRelativeDateTodayTomorrow = todayTomorrowConfig.some(
    (day) => day === relativeDate
  );
  if (!relativeDate) {
    return 'Enrolments have closed for this test series';
  }
  if (isRelativeDateTodayTomorrow) {
    return `Enrolment ends ${relativeDate}`;
  }
  if (relativeDate <= 7 && relativeDate > 0) {
    return `Enrolment ends in ${relativeDate} days`;
  }
  return `Enrolment ends on ${relativeDate}`;
};

export const getOfferMessage = (date) => {
  if (!date) return 'N/A';
  const relativeDate = getRelativeDayOrCurrentDateWithTDays(
    date,
    true,
    7,
    true
  );
  const isRelativeDateTodayTomorrow = todayTomorrowConfig.some(
    (day) => day === relativeDate
  );
  if (!relativeDate) {
    return 'Offer is over for this test series';
  }
  if (isRelativeDateTodayTomorrow) {
    return `Offer ends ${relativeDate}`;
  }
  if (relativeDate <= 7 && relativeDate > 0) {
    return `Offer ends in ${relativeDate} days`;
  }
  return `Offer ends on ${relativeDate}`;
};

export const getSkuBasedEventProps = (activeSku, testSeries) => {
  return {
    entity_content_type: 'Test Series',
    entity_title: testSeries?.name,
    entity_uid: testSeries?.uid,
    last_primary_source: 'Store',
    subscription_type: 'Store Subscription',
    offer_price: activeSku?.price,
    offer_end_date: activeSku?.priceEffectiveTill ?? 'NA',
    subscription_price: activeSku?.maxRetailPrice || activeSku?.price,
    subscription_duration: getDateDifference(activeSku?.accessEndDate)
  };
};

export const getCbtTestTypeLabel = (programmeComposition) => {
  if (programmeComposition === PROGRAMME_COMPOSITION.ONLINE_OFFLINE) {
    return 'Online + Offline';
  }
  if (programmeComposition === PROGRAMME_COMPOSITION.ONLINE) {
    return 'Online';
  }
  return 'NA';
};

export const getCbtUserDetailsSubmittedEventProps = ({
  venueSelection,
  goalSegmentData = {},
  entityProps = {},
  programmeComposition
}) => ({
  ...goalSegmentData,
  ...entityProps,
  ...(programmeComposition != null && {
    cbt_test_type: getCbtTestTypeLabel(programmeComposition)
  }),
  ...(venueSelection && {
    learner_name: venueSelection.fullName?.trim(),
    selected_city: venueSelection.city,
    selected_state: venueSelection.state,
    selected_centre: venueSelection.centreName
  })
});

const STORE_TEST_SERIES_REFERRAL_CODE_KEY = 'storeTestSeriesReferralCodes';

export const getTestSeriesReferralInStorage = (uid) => {
  try {
    const referralCodesStr = getItemFromSessionStorage(
      STORE_TEST_SERIES_REFERRAL_CODE_KEY
    );

    const referralCodes = !!referralCodesStr && JSON.parse(referralCodesStr);

    return referralCodes && referralCodes[uid];
  } catch {
    return null;
  }
};

export const setTestSeriesReferralInStorage = (uid, code) => {
  try {
    const referralCodesStr = getItemFromSessionStorage(
      STORE_TEST_SERIES_REFERRAL_CODE_KEY
    );

    const referralCodes = !!referralCodesStr && JSON.parse(referralCodesStr);
    sessionStorage.setItem(
      STORE_TEST_SERIES_REFERRAL_CODE_KEY,
      JSON.stringify({ ...referralCodes, [uid]: code })
    );
  } catch {
    return null;
  }
};

export const getFirstLetterForIcon = (text = '') => {
  const trimmedText = text.trim();
  return trimmedText.charAt(0) || 'A';
};
